import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoadingState from "./components/LoadingState";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const ModuleSelectorPage = lazy(() => import("./pages/ModuleSelectorPage"));
const DashboardPage = lazy(() => import("./modules/pagos/pages/DashboardPage"));
const InvoicesPage = lazy(() => import("./modules/pagos/pages/InvoicesPage"));
const KanbanPage = lazy(() => import("./modules/pagos/pages/KanbanPage"));
const AuditoriaPage = lazy(() => import("./modules/pagos/pages/AuditoriaPage"));
const TransaccionesPage = lazy(() => import("./modules/pagos/pages/TransaccionesPage"));
const ReportesFacturasPage = lazy(() => import("./modules/pagos/pages/ReportesFacturasPage"));
const AutorizacionesDashboardPage = lazy(() => import("./modules/autorizaciones/pages/DashboardPage"));
const MisSolicitudesPage = lazy(() => import("./modules/autorizaciones/pages/MisSolicitudesPage"));
const PorAprobarPage = lazy(() => import("./modules/autorizaciones/pages/PorAprobarPage"));
const AdminFlujosPage = lazy(() => import("./modules/autorizaciones/pages/AdminFlujosPage"));
const AdminNotificacionesPage = lazy(() => import("./modules/autorizaciones/pages/AdminNotificacionesPage"));
const PagosLayout = lazy(() => import("./modules/pagos/components/PagosLayout"));
const AutorizacionesLayout = lazy(() => import("./modules/autorizaciones/components/AutorizacionesLayout"));
const CoreLayout = lazy(() => import("./modules/core/components/CoreLayout"));
const UsuariosPage = lazy(() => import("./modules/core/pages/UsuariosPage"));
const EmpresasPage = lazy(() => import("./modules/core/pages/EmpresasPage"));
const RolesPermisosPage = lazy(() => import("./modules/core/pages/RolesPermisosPage"));
const ModulosPage = lazy(() => import("./modules/core/pages/ModulosPage"));
const BitacoraPage = lazy(() => import("./modules/core/pages/BitacoraPage"));
const ProveedoresPage = lazy(() => import("./modules/core/pages/ProveedoresPage"));
const RhDashboardPage = lazy(() => import("./modules/rh_nomina/pages/DashboardPage"));

const PageLoader = () => <LoadingState fullScreen showBackground sizeClass="h-12 w-12" />;

const ProtectedRoute = ({ children, permiso, requireGlobal, redirectTo = '/dashboard' }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingState fullScreen showBackground sizeClass="h-12 w-12" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (permiso && !user.es_global && !user.permisos?.includes(permiso)) {
    return <Navigate to={redirectTo} replace />;
  }

  if (requireGlobal && !user.es_global) {
    return <Navigate to="/autorizaciones/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/modulos" replace /> : <LoginPage />} />

      <Route path="/modulos" element={
        <ProtectedRoute>
          <ModuleSelectorPage />
        </ProtectedRoute>
      } />

      <Route path="/rh-nomina" element={
        <ProtectedRoute>
          <RhDashboardPage />
        </ProtectedRoute>
      } />

      <Route path="/" element={
        <ProtectedRoute>
          <PagosLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="/modulos" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="kanban" element={
          <ProtectedRoute permiso="pagos.ver_kanban">
            <KanbanPage />
          </ProtectedRoute>
        } />
        <Route path="auditoria" element={
          <ProtectedRoute permiso="pagos.ver_auditoria">
            <AuditoriaPage />
          </ProtectedRoute>
        } />
        <Route path="transacciones" element={
          <ProtectedRoute permiso="pagos.cambiar_estatus">
            <TransaccionesPage />
          </ProtectedRoute>
        } />
        <Route path="reportes" element={
          <ProtectedRoute permiso="pagos.ver_reportes">
            <ReportesFacturasPage />
          </ProtectedRoute>
        } />
        <Route path="proveedores" element={
          <ProtectedRoute permiso="core.administrar_proveedores">
            <ProveedoresPage />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="/autorizaciones" element={
        <ProtectedRoute>
          <AutorizacionesLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AutorizacionesDashboardPage />} />
        <Route path="mis-solicitudes" element={<MisSolicitudesPage />} />
        <Route path="por-aprobar" element={
          <ProtectedRoute permiso="autorizaciones.ver_pendientes_aprobar" redirectTo="/autorizaciones/dashboard">
            <PorAprobarPage />
          </ProtectedRoute>
        } />
        <Route path="admin/flujos" element={
          <ProtectedRoute permiso="autorizaciones.administrar_flujos" redirectTo="/autorizaciones/dashboard">
            <AdminFlujosPage />
          </ProtectedRoute>
        } />
        <Route path="admin/notificaciones" element={
          <ProtectedRoute permiso="autorizaciones.administrar_flujos" redirectTo="/autorizaciones/dashboard">
            <AdminNotificacionesPage />
          </ProtectedRoute>
        } />
      </Route>

      <Route path="/core" element={
        <ProtectedRoute requireGlobal>
          <CoreLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Navigate to="usuarios" replace />} />
        <Route path="usuarios" element={<UsuariosPage />} />
        <Route path="empresas" element={<EmpresasPage />} />
        <Route path="roles-permisos" element={<RolesPermisosPage />} />
        <Route path="modulos" element={<ModulosPage />} />
        <Route path="bitacora" element={<BitacoraPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/modulos" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Suspense fallback={<PageLoader />}>
          <AppRoutes />
        </Suspense>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
